import { useState, useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { buildApiUrl } from '../config';

export const useSignalR = (hubUrl = buildApiUrl('/opportunityHub')) => {
  const [connection, setConnection] = useState(null);
  const [connectionState, setConnectionState] = useState('Disconnected');
  const [opportunities, setOpportunities] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef(null);

  const transformOpportunityData = (apiData) => {
    return {
      id: apiData.id,
      coin: apiData.asset,
      coinId: apiData.assetId ?? apiData.asset_id ?? apiData.coinId ?? apiData.id ?? null,
      fromExchange: apiData.buyExchange,
      toExchange: apiData.sellExchange,
      askPrice: apiData.buyPrice,
      bidPrice: apiData.sellPrice,
      askVolume: apiData.buyVolume,
      askVolumeIdr: apiData.buyVolumeIdr,
      bidVolume: apiData.sellVolume,
      bidVolumeIdr: apiData.sellVolumeIdr,
      profit: apiData.profitPct,
      action: 'buy',
    };
  };

  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    connectionRef.current = newConnection;

    newConnection.onreconnecting(() => {
      setConnectionState('Reconnecting');
      setIsConnected(false);
    });

    newConnection.onreconnected(() => {
      setConnectionState('Connected');
      setIsConnected(true);
    });

    newConnection.onclose(() => {
      setConnectionState('Disconnected');
      setIsConnected(false);
    });

    newConnection.on('ArbitrageSnapshot', (snapshot) => {
      const transformedOpportunities = snapshot.map(transformOpportunityData);
      setOpportunities(transformedOpportunities);
    });

    newConnection.on('OpportunityUpdate', (patch) => {
      setOpportunities(prev => {
        let updated = [...prev];

        if (patch.removed && patch.removed.length > 0) {
          const removedKeys = patch.removed;
          updated = updated.filter(op => {
            return !removedKeys.includes(op.id);
          });
        }

        if (patch.updated && patch.updated.length > 0) {
          patch.updated.forEach(updatedOp => {
            const transformed = transformOpportunityData(updatedOp);
            const index = updated.findIndex(op => op.id === transformed.id);
            if (index !== -1) {
              updated[index] = transformed;
            }
          });
        }

        if (patch.added && patch.added.length > 0) {
          const newOpportunities = patch.added.map(transformOpportunityData);
          updated = [...updated, ...newOpportunities];
        }

        return updated;
      });
    });

    const startConnection = async () => {
      try {
        setConnectionState('Connecting');
        await newConnection.start();
        setConnectionState('Connected');
        setIsConnected(true);
        setConnection(newConnection);
      } catch (error) {
        setConnectionState('Disconnected');
        setIsConnected(false);
        setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [hubUrl]);

  const refreshData = async () => {
    if (connection && isConnected) {
      try {
        await connection.invoke('RequestSnapshot');
      } catch (error) {
      }
    }
  };

  return {
    connection,
    connectionState,
    isConnected,
    opportunities,
    refreshData
  };
};
